import { EmployeeAccessLevel, EmployeeFeature, EmployeeRecordingMode } from './Employee'
import { CustomerFeature, CustomerSkin } from './Customer'

export interface LoginRequest {
	/** Username - email or name */
	username: string
	/** Users password */
	password: string
	/** If SMS authentication is required, this should be the SMS code sent to the users phone */
	smsKey?: string
	/** If authenticator validation is required, this is the time based token generated on the users device. */
	authenticatorToken?: string
	/** True if the server should send a cookie with the token, default false. */
	cookie: boolean
	/** True if the token shall not expire until the user explicitly log out. For use in apps. */
	persist: boolean
}

/**
 * Login response
 */
export interface LoginResponse {
	/** Token to be used in the Authorization header (only on login) */
	token?: string
	/** Minutes until the token expires if idle */
	expiresInMinutes: number
	/** Id of employee */
	employee: string
	/** Language of employee, en or da */
	language: string
	/** Id of customer employee belongs to */
	customer: string
	/** Name of employee */
	name: string
	/** Access level of employee */
	accessLevel: EmployeeAccessLevel
	/** Name of customer */
	customerName: string
	/** List of features customer has */
	customerFeatures: CustomerFeature[]
	/** Integration setting on the customer */
	customerIntegrations: {
		billing?: {
			economic?: {
				enabled?: boolean
			},
			rackbeat?: {
				enabled?: boolean
			}
		},
		licenses?: {
			also?: {
				enabled?: boolean
			}
		},
		ms365?: {
			enabled?: boolean
		},
		mvnoPrivate?: {
			enabled?: boolean
		}
	}
	/** Id of the customers parent reseller, if customer is not itself the reseller */
	reseller?: string
	/** Accounting system id, optional */
	accountingSystemId?: string
	/** List of features the employee has */
	employeeFeatures: EmployeeFeature[]
	/** Recording mode */
	recordingMode: EmployeeRecordingMode
	/** Number of days recordings are kept */
	recordingRetension: number
	/** Reseller choosen skin for the customer */
	skin: CustomerSkin
	/** If session is an impersonated one, this is the id of the employee who is doing the impersonation */
	impersonatedBy?: string
	/** True if employee has access to Communicator */
	communicatorAccess: boolean
	/** True if employee has access to Recording */
	recordingAccess: boolean
}

/**
 * Impersonate response
 */
export type ImpersonateResponse = LoginResponse

/**
 * Validate token response with extended data
 */
export interface ValidateTokenExtendedResponse extends LoginResponse {
	/** True on success */
	success: boolean
}

/** Validate token response */
export interface ValidateTokenResponse {
	/** True on success */
	success: boolean
	/** Id of employee */
	employee: string
	/** Id of customer employee belongs to */
	customer: string
	/** Access level of employee */
	accessLevel: EmployeeAccessLevel
}

export interface ResetRequest {
	/** Email address of the account to send a reset link for */
	username: string
	/** Where to send the reset link to: EMAIL or SMS */
	method: 'EMAIL' | 'SMS'
}

export interface ResetResponse {
	/** True on success */
	success: boolean
	/** How the reset link was sent: EMAIL or SMS */
	method: 'EMAIL' | 'SMS'
}

/** Error codes that is returned in path/code */
export enum LoginResponseErrorCodes {
	/** The supplied username and/or password is invalid */
	credentials_invalid = 'credentials_invalid',
	/** The users account has been permanently disabled, login is not possible */
	account_disabled = 'account_disabled',
	/** Account has access level 'NO_LOGIN' */
	no_login = 'no_login',
	/** Internal server error */
	internal_error = 'internal_error',
	/** The user cannot login from the current location */
	ipaddress_invalid = 'ipaddress_invalid',
	/** SMS authentication is required, but the SMS with the key could not be sent */
	sms_error = 'sms_error',
	/** Please authenticate using SMS key sent to your mobile phone */
	sms_authenticate = 'sms_authenticate',
	/** The SMS key used for authentication is not valid */
	sms_key_invalid = 'sms_key_invalid',
	/** Please authenticate using one time token generated on your device */
	authenticator_authenticate = 'authenticator_authenticate',
	/** The authenticator key used for authentication is not valid */
	authenticator_key_invalid = 'authenticator_key_invalid',
	/** Please store the provided key in your authenticator and provide the generated token. */
	authenticator_setup = 'authenticator_setup',
	/** The account may only authenticate with a passkey */
	webauthn_only = 'webauthn_only'
}

export enum ResetResponseErrorCodes {
	/** The users account has been permanently disabled, login is not possible */
	account = 'account',
	/** Employee is using 2-factor authentication - reset not possbile */
	username = 'username',
	/** A password reset request already exists, try again in 30 minutes */
	time = 'time'
}
